<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :no-results-text="noResultsText"
    :external-search="true"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />
</template>

<script>
import { mapState } from "pinia";
import { useTripCargoStore } from "@/stores/tripCargoStore";

export default {
  name: "CargoSelect",

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    tripId: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите груз",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    noResultsText: {
      type: String,
      default: "Грузы не найдены",
    },
    autoSelectFirst: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["update:modelValue", "change"],

  data() {
    return {
      preselectedOption: null,
      isSearchActive: false,
      isFirstLoad: true,
    };
  },

  computed: {
    ...mapState(useTripCargoStore, [
      "cargos",
      "isLoading",
      "pagination",
    ]),

    formattedOptions() {
      const options = this.cargos.map((cargo) => ({
        value: cargo.id,
        label: `Груз #${cargo.number || cargo.id}`,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    tripId: {
      handler(newTripId) {
        if (newTripId) {
          this.isFirstLoad = true;
          this.loadCargos();
        } else {
          const store = useTripCargoStore();
          store.cargos = [];
          this.preselectedOption = null;
          this.isFirstLoad = true;
        }
      },
      immediate: true,
    },

    formattedOptions: {
      handler(newOptions) {
        if (
          this.autoSelectFirst &&
          this.isFirstLoad &&
          newOptions.length > 0 &&
          !this.modelValue
        ) {
          this.$nextTick(() => {
            const firstValue = newOptions[0].value;
            this.$emit("update:modelValue", firstValue);
            this.$emit("change", firstValue);
            this.isFirstLoad = false;
          });
        }
      },
    },

    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }
      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }
      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      const store = useTripCargoStore();
      await store.fetchCargos(this.tripId, {
        search: searchData.query,
      });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      if (!this.tripId) {
        const store = useTripCargoStore();
        store.cargos = [];
        return;
      }
      const store = useTripCargoStore();
      await store.fetchCargos(this.tripId);
    },

    async handleScrollEnd() {
      const store = useTripCargoStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.isLoading
      ) {
        await store.loadMoreCargos(this.tripId);
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useTripCargoStore();
      const exists = store.cargos.find(
        (c) => String(c.id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        const cargo = await store.showCargo(this.modelValue);
        if (cargo?.id) {
          this.preselectedOption = {
            value: cargo.id,
            label: `Груз #${cargo.number || cargo.id}`,
          };
        }
      } catch (error) {
        console.error("Ошибка при загрузке выбранного груза:", error);
      }
    },

    async loadCargos() {
      if (!this.tripId) return;
      const store = useTripCargoStore();
      await store.fetchCargos(this.tripId);
      await this.fetchPreselectedOption();
    },
  },
};
</script>
